import classNames from 'classnames';
import {
  type InputHTMLAttributes,
  type ReactNode,
  useId,
  useState,
} from 'react';

import { Label } from '../label/Label';
import styles from './Radio.module.css';
import { useRadioGroupContext } from './RadioGroupContext';
import { RadioIndicator } from './RadioIndicator';

export enum RadioLabelSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  /** Label text. Alternatively pass `children` (e.g. rich label content). */
  label?: ReactNode;
  children?: ReactNode;
  size?: RadioLabelSize;
  /**
   * Label weight. Defaults to `'semibold'` (title style, for radios that have a
   * description beneath). Use `'normal'` for a plain label-only radio.
   */
  labelFontWeight?: 'normal' | 'semibold';
  'data-testid'?: string;
}

export const Radio = ({
  label,
  children,
  disabled,
  className,
  size = RadioLabelSize.MD,
  labelFontWeight = 'semibold',
  style,
  id,
  name,
  value,

  checked,
  defaultChecked,
  onChange,
  onFocus,
  onBlur,

  'data-testid': dataTestId,
  ...inputProps
}: RadioProps) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  // When rendered inside a RadioGroup (and given a `value`), the group owns the
  // selection: this radio is controlled, checked when the group's value matches.
  const group = useRadioGroupContext();
  const inGroup = group !== null && value !== undefined;

  const isControlled = inGroup || checked !== undefined;
  const resolvedChecked = inGroup ? group.value === String(value) : checked;

  // Internal state for uncontrolled mode only; controlled mode reads the prop.
  const [internalChecked, setInternalChecked] = useState<boolean>(
    defaultChecked ?? false
  );

  const isChecked = isControlled ? !!resolvedChecked : internalChecked;

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <label
      className={classNames(
        styles.container,
        disabled && styles.disabled,
        className
      )}
      style={style}
      htmlFor={disabled ? undefined : controlId}
      data-testid={dataTestId}
      onClick={disabled ? (e) => e.preventDefault() : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        {...inputProps}
        type="radio"
        id={controlId}
        className={styles.input}
        name={inGroup ? group.name : name}
        value={value}
        disabled={disabled}
        checked={isControlled ? !!resolvedChecked : internalChecked}
        onChange={(e) => {
          if (inGroup && value !== undefined) {
            group.onChange(String(value));
          } else if (!isControlled) {
            setInternalChecked(e.target.checked);
          }
          onChange?.(e);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e);
        }}
      />

      <RadioIndicator
        checked={isChecked}
        disabled={disabled}
        isHovered={isHovered}
        isFocused={isFocused}
      />

      {(label ?? children) != null && (
        <Label
          as="span"
          disabled={disabled}
          size={size}
          fontWeight={labelFontWeight}
        >
          {label ?? children}
        </Label>
      )}
    </label>
  );
};
